// LivestreamOverlaySwiper.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Swiper, SwiperPaginationDefaultStyle } from '../component/Swiper.jsx';
import { color, breakpoint } from '../style/variables.js';

import media, { mediaHeight } from '../style/media.js';

const SLIDE_GAP_MOBILE = 12;
const SLIDE_GAP_TABLET = 32;

const SLIDE_WIDTH_DEFAULT = 443.25;
const SLIDE_WIDTH_TABLET = 465.75;
const SLIDE_WIDTH_MOBILE = 289.12;
const SLIDE_WIDTH_MOBILE_LANDSCAPE = 427;
const SLIDE_HEIGHT_DEFAULT = 788; // 443.25 * 16 / 9
const SLIDE_HEIGHT_TABLET = 828; // 465.75 * 16 / 9
const SLIDE_HEIGHT_MOBILE = 514; // 289.12 * 16 / 9
const SLIDE_HEIGHT_MOBILE_LANDSCAPE = 242; // design spec height for 427:242
const LANDSCAPE_VERTICAL_OFFSET = 16;

const HEADER_OFFSET = 112; // header height (64px) + gap (24px) * 2

export const LivestreamOverlaySwiper = ({ children }) => {
  const hasChildren = React.Children.count(children) > 0;

  if (!hasChildren) {
    return (
      <StyledLivestreamOverlaySwiper>
        <Placeholder />
      </StyledLivestreamOverlaySwiper>
    );
  }

  return (
    <StyledLivestreamOverlaySwiper>
      <SwiperWrapper
        modules={['Pagination']}
        slidesPerView={'auto'}
        centeredSlides={true}
        spaceBetween={SLIDE_GAP_MOBILE}
        resistance={true}
        resistanceRatio={0.5}
        breakpoints={{
          [breakpoint.mobile]: {
            spaceBetween: SLIDE_GAP_TABLET,
          },
        }}
        pagination={{
          type: 'bullets',
        }}
      >
        {children}
      </SwiperWrapper>
    </StyledLivestreamOverlaySwiper>
  );
};

LivestreamOverlaySwiper.propTypes = {
  children: PropTypes.node,
};

// Maintain 9:16 aspect ratio across all viewports.
// width uses min() to also respect the height constraint, so the ratio is preserved
// even when the viewport is too short — instead of clipping height, width shrinks.
const SwiperWrapperStyle = css`
  aspect-ratio: 9 / 16;
  width: min(
    ${SLIDE_WIDTH_DEFAULT}px,
    calc((100vh - ${HEADER_OFFSET}px) * 9 / 16)
  );
  max-height: calc(100vh - ${HEADER_OFFSET}px);

  /* Fallback for browsers that don't support aspect-ratio (e.g. iOS 14 Safari).
     The previous padding-top + height: 0 hack caused the overlay CTA children
     to lose a reliable height basis, so use explicit dimensions instead. */
  @supports not (aspect-ratio: 1 / 1) {
    width: ${SLIDE_WIDTH_DEFAULT}px;
    height: ${SLIDE_HEIGHT_DEFAULT}px;
    max-height: ${SLIDE_HEIGHT_DEFAULT}px;
  }
  ${media.tablet`
    width: min(${SLIDE_WIDTH_TABLET}px, calc((100vh - ${HEADER_OFFSET}px) * 9 / 16));
    @supports not (aspect-ratio: 1 / 1) {
      width: ${SLIDE_WIDTH_TABLET}px;
      height: ${SLIDE_HEIGHT_TABLET}px;
      max-height: ${SLIDE_HEIGHT_TABLET}px;
    }
  `}
  ${media.mobile`
    width: min(clamp(${SLIDE_WIDTH_MOBILE}px, calc(100vw - ${HEADER_OFFSET}px), ${SLIDE_WIDTH_TABLET}px), calc((100vh - ${HEADER_OFFSET}px) * 9 / 16));
    @supports not (aspect-ratio: 1 / 1) {
      width: ${SLIDE_WIDTH_MOBILE}px;
      height: ${SLIDE_HEIGHT_MOBILE}px;
      max-height: ${SLIDE_HEIGHT_MOBILE}px;
    }
  `}
  ${mediaHeight.mobileLandscape`
    @media (orientation: landscape) {
      aspect-ratio: 427 / 242; // design spec: W427 × H242 
      width: min(${SLIDE_WIDTH_MOBILE_LANDSCAPE}px, calc((100vh - ${LANDSCAPE_VERTICAL_OFFSET}px) * 427 / 242));
      max-height: calc(100vh - ${LANDSCAPE_VERTICAL_OFFSET}px);
      @supports not (aspect-ratio: 1 / 1) {
        width: ${SLIDE_WIDTH_MOBILE_LANDSCAPE}px;
        height: ${SLIDE_HEIGHT_MOBILE_LANDSCAPE}px;
        max-height: ${SLIDE_HEIGHT_MOBILE_LANDSCAPE}px;
      }
    }
  `}
`;

const StyledLivestreamOverlaySwiper = styled.div`
  width: 100%;
  max-height: 100vh;
`;

const SwiperWrapper = styled(Swiper)`
  ${SwiperPaginationDefaultStyle};

  .swiper-wrapper {
    ${media.tablet`
      padding-bottom: 22px;
    `}
    ${media.mobile`
      @media (orientation: landscape) {
        padding-bottom: 16px;
      }
    `}
  }

  .swiper-slide {
    ${SwiperWrapperStyle}
    position: relative;
    border: 1px solid ${color.neutral['20_alpha_40']};
    border-radius: 8px;
    box-shadow: 0px 4px 16px 2px ${color.neutral['0_alpha_20']};

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 5;
      border-radius: 8px;
      background-color: ${color.neutral[10]};
      opacity: 60%;
      // Block taps on inactive slides (e.g. chat / mini profile under the mask).
      pointer-events: auto;
      transition: opacity 300ms ease;
    }
  }

  .swiper-slide-active {
    &::after {
      opacity: 0;
      // Let taps reach slide content
      pointer-events: none;
    }
  }
`;

const Placeholder = styled.div`
  ${SwiperWrapperStyle}
  border-radius: 8px;
  background: ${color.neutral[10]};
  margin: 0 auto;
`;

export default LivestreamOverlaySwiper;
